import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBrands, getBrandsAlphabetically } from '../services/brandService';
import { toHandle } from '../utils/handle';

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [active, setActive] = useState(null);

  useEffect(() => {
    getBrands().then(setBrands).catch(() => {});
    getBrandsAlphabetically().then(setGrouped).catch(() => {});
  }, []);

  const letters = Object.keys(grouped).sort();

  const scrollTo = (l) => {
    setActive(l);
    document.getElementById(`letter-${l}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>All Brands — Skinn Thrive</title>
        <meta name="description" content="Browse all skincare and haircare brands at Skinn Thrive — authentic products from trusted derma brands." />
        <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/brands`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Skinn Thrive" />
        <meta property="og:title" content="All Brands — Skinn Thrive" />
        <meta property="og:description" content="Browse all skincare and haircare brands at Skinn Thrive — authentic products from trusted derma brands." />
        <meta property="og:url" content={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/brands`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="All Brands — Skinn Thrive" />
        <meta name="twitter:description" content="Browse all skincare and haircare brands at Skinn Thrive — authentic products from trusted derma brands." />
      </Helmet>

      <div className="py-10 md:py-14 px-4 text-center border-b border-border"
           style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)' }}>
        <p className="eyebrow mb-2">Skinn Thrive</p>
        <h1 className="display-title text-3xl md:text-5xl">All Brands</h1>
        <p className="text-ink-soft mt-3 text-sm">
          {brands.length ? `We carry ${brands.length} authentic derma brand${brands.length !== 1 ? 's' : ''}` : 'Authentic derma brands'}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Brand name cards (the "ABB / AJA" initials looked unfinished) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-14">
          {brands.map((brand) => {
            const count = brand.productCount ?? brand.count ?? 0;
            return (
              <Link
                key={brand.id}
                to={`/collections/${brand.slug || toHandle(brand.name)}`}
                className="group flex items-center justify-between gap-3 p-4 md:p-6 bg-white border border-[#EDEAF2] rounded-2xl
                           transition-all duration-200 hover:border-primary/30 hover:shadow-card-hover"
              >
                <div className="min-w-0">
                  <p className="font-display text-lg md:text-2xl font-semibold text-ink truncate group-hover:text-primary transition-colors">{brand.name}</p>
                  <p className="text-[11px] md:text-xs text-ink-muted mt-0.5">{count} product{count !== 1 ? 's' : ''}</p>
                </div>
                <svg className="w-4 h-4 text-ink-muted group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0"
                     fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                </svg>
              </Link>
            );
          })}
        </div>

        {/* A–Z index only pays off with many brands; with a handful it just repeated the cards above */}
        {brands.length > 12 && <>
        {/* Alphabetical nav */}
        {letters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8 sticky top-16 bg-white/95 backdrop-blur-sm py-3 -mx-4 px-4 sm:-mx-6 sm:px-6 z-10 border-b border-border">
            {letters.map(l => (
              <button
                key={l}
                onClick={() => scrollTo(l)}
                className={`w-9 h-9 rounded-xl text-sm font-bold transition-all
                  ${active === l
                    ? 'bg-[#5B21B6] text-white shadow-sm'
                    : 'bg-surface text-[#5B21B6] hover:bg-primary-light border border-border'}`}
              >
                {l}
              </button>
            ))}
          </div>
        )}

        {/* Alphabetical listing */}
        <div className="space-y-10">
          {letters.map(letter => (
            <div key={letter} id={`letter-${letter}`} className="scroll-mt-28">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#5B21B6] flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-sm">{letter}</span>
                </div>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {(grouped[letter] || []).map((brand) => (
                  <Link
                    key={brand.id}
                    to={`/collections/${brand.slug || toHandle(brand.name)}`}
                    className="group flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-100
                               hover:border-[#5B21B6] hover:bg-[#F5F3FF] transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm group-hover:text-[#5B21B6] transition-colors">{brand.name}</p>
                      <p className="text-xs text-gray-400">{brand.productCount ?? brand.count ?? 0} products</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-300 group-hover:text-[#5B21B6] transition-colors shrink-0"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        </>}
      </div>
    </>
  );
};

export default BrandsPage;
